import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useReserveApi } from '@store/api/reserveApi';
import { useAuthApi } from '@store/api/authApi';
import { useUserStore } from '@store/user';
import type { ReservePack, ReserveRemaining, ReserveState, ReserveWallet, ReserveWhitelist } from '@store/api/interfaces';

/**
 * Состояние «Резервного подключения» для кабинета: ответ `GET /reserve` и каталог
 * пакетов плюс производные признаки, на которые смотрят карточка в аккаунте и
 * страница резерва.
 *
 * Загрузка намеренно best-effort: резерв — дополнение к подписке, и его
 * недоступность (выключенные продажи, невыкаченная миграция бота, молчащий
 * keymanager) не должна ломать кабинет.
 */
export const useReserveStore = defineStore('reserve', () => {
	const reserveApi = useReserveApi();
	const authApi = useAuthApi();

	const state = ref<ReserveState | null>(null);
	const packs = ref<ReservePack[]>([]);

	// Один запрос на несколько одновременных вызовов load()/loadPacks().
	let statePending: Promise<void> | null = null;
	let packsPending: Promise<void> | null = null;

	const wallet = computed<ReserveWallet | null>(() => state.value?.wallet ?? null);
	const whitelist = computed<ReserveWhitelist | null>(() => state.value?.whitelist ?? null);
	const remaining = computed<ReserveRemaining | null>(() => state.value?.remaining ?? null);

	/**
	 * Резерв выдан и его нужно показывать. Единственный источник — кошелёк бота:
	 * пустой блок keymanager означает «нет данных», а не «резерва нет».
	 * `issued` первичен, состояние проверяется запасным путём — на случай бэкенда,
	 * который поле ещё не отдаёт.
	 */
	const isIssued = computed<boolean>(() => {
		const value = wallet.value;
		if (!value) return false;
		if (typeof value.issued === 'boolean') return value.issued;
		return value.state === 'active' || value.state === 'grace';
	});

	// Подписка кончилась: резерв и остаток сгорят, докупать ГБ уже нельзя.
	const isGrace = computed<boolean>(() => wallet.value?.state === 'grace');

	// Подписка истекла и всё сгорело: резерва нет, но страница показывает
	// «Сохранено 0 ГБ» и ведёт продлевать подписку
	const isBurned = computed<boolean>(() => wallet.value?.state === 'burned');

	// Подписка неактивна: остаток либо ещё хранится (grace), либо уже сгорел
	const isInactive = computed<boolean>(() => isGrace.value || isBurned.value);
	const graceUntil = computed<string | null>(() => wallet.value?.grace_until ?? null);
	const monthlyGb = computed<number>(() => wallet.value?.monthly_gb ?? 0);

	const isLimited = computed<boolean>(() => whitelist.value?.status === 'LIMITED');
	const hasWhitelistData = computed<boolean>(() => !!whitelist.value);

	// keymanager не ответил или резервной подписки ещё нет: остаток посчитан при
	// нулевом расходе, значит показывать его нужно с оговоркой.
	const isApproximate = computed<boolean>(() => {
		if (!state.value) return false;
		return !state.value.key_manager_available || !whitelist.value;
	});

	const remainingBytes = computed<number>(() => remaining.value?.total_remaining_bytes ?? 0);

	/**
	 * Знаменатель строки «Потрачено X из Y ГБ» — лимит, который бот выставил в панели.
	 *
	 * Кошелёк бота хранит только приращения цикла: F (`free_granted_bytes`) и
	 * P (`purchased_bytes`). Счётчик панели накопительный (NO_RESET), поэтому лимит
	 * там стоит от показаний счётчика на момент последнего события с кошельком:
	 * U0 + F + P. Считать знаменатель как F + P значит занижать и «из», и
	 * «потрачено» (числитель = знаменатель − остаток) ровно на U0, поэтому берём
	 * готовый лимит панели — он приходит в том же ответе.
	 *
	 * Запасные варианты нужны там, где лимита панели нет или он не про месяц:
	 * блока `whitelist` нет (keymanager молчит или ключ не создан), лимит 0
	 * (в панели это «безлимит») и лимит меньше остатка (счётчик сбрасывали мимо
	 * бота) — тогда остаётся F + P, как было.
	 *
	 * Из-за этого в двух случаях цифра чуть расходится с ботом, и это принято
	 * сознательно, чтобы не менять API: при перерасходе бот покажет «15 из 15»,
	 * а панель даст «16 из 16»; при сбросе счётчика мимо бота — F + P.
	 */
	const grantedBytes = computed<number>(() => {
		const value = wallet.value;
		if (!value) return 0;

		// Пустой кошелёк (ничего не выдано и не куплено): лимит панели тогда равен
		// одному U0, и без этой проверки вышло бы «0,4 из 0,4 ГБ» с полной полосой —
		// то есть «трафик кончился» там, где его и не выдавали. Ноль оставляет
		// прежнее «0 из 0 ГБ» без полосы. Саму карточку это не прячет: её
		// видимость (isVisible) зависит только от состояния кошелька.
		const walletBytes = (value.free_granted_bytes ?? 0) + (value.purchased_bytes ?? 0);
		if (walletBytes <= 0) return 0;

		const panelLimit = whitelist.value?.traffic_limit_bytes ?? 0;
		if (panelLimit > 0 && panelLimit >= remainingBytes.value) return panelLimit;

		return walletBytes;
	});

	// Что хранится без подписки: только оплаченные, бесплатные сгорают при истечении
	const savedBytes = computed<number>(() => remaining.value?.purchased_remaining_bytes ?? 0);

	/**
	 * Доля оставшегося трафика для полосы, 0–100.
	 *
	 * null — рисовать нечего: выдано ноль (полоса делилась бы на ноль) или счётчиков
	 * вовсе нет. Значение подрезано с обеих сторон: бот и keymanager считают остаток
	 * независимо и в момент начисления могут разойтись, а полоса шире собственной
	 * дорожки выглядит как ошибка вёрстки.
	 */
	const remainingPercent = computed<number | null>(() => {
		if (!remaining.value || grantedBytes.value <= 0) return null;
		return Math.min(100, Math.max(0, (remainingBytes.value / grantedBytes.value) * 100));
	});

	// Полоса заполняется по мере расхода: пустая — ничего не потрачено,
	// полная — трафик кончился. Считается от остатка, чтобы сходиться с текстом
	// «Осталось X из Y» рядом.
	const usedPercent = computed<number | null>(() => {
		if (remainingPercent.value === null) return null;
		return 100 - remainingPercent.value;
	});

	// Трафик кончился: так сказал keymanager (LIMITED) или остаток по счётчикам
	// нулевой — второе на случай, когда keymanager ещё не успел ограничить.
	const isExhausted = computed<boolean>(() => {
		return isLimited.value || (!!remaining.value && remainingBytes.value <= 0);
	});

	const nextAccrualAt = computed<string | null>(() => wallet.value?.next_accrual_at ?? null);

	/**
	 * Что ждёт резерв дальше — для подписи под названием карточки.
	 * «Закончится» — в grace (сгорит) и когда подписка истекает раньше
	 * следующего начисления: обновления не будет. Иначе — «обновится» с датой
	 * начисления. Без даты подписи нет.
	 */
	const nextEvent = computed<{ kind: 'renews' | 'ends'; at: string } | null>(() => {
		if (isGrace.value) {
			return graceUntil.value ? { kind: 'ends', at: graceUntil.value } : null;
		}
		const accrual = nextAccrualAt.value;
		if (!accrual) return null;

		const subscriptionEnd = useUserStore().user?.subscription?.end;
		if (subscriptionEnd) {
			const endTs = Date.parse(subscriptionEnd);
			const accrualTs = Date.parse(accrual);
			if (!Number.isNaN(endTs) && !Number.isNaN(accrualTs) && endTs < accrualTs) {
				return { kind: 'ends', at: subscriptionEnd };
			}
		}
		return { kind: 'renews', at: accrual };
	});

	/**
	 * Можно ли купить пакет — признак сервера, фронт его не выводит сам: бэкенд
	 * считает его от подписки (оплаченная и действующая), а не от состояния
	 * кошелька, так что покупать может и тот, кому резерв ещё не выдан.
	 */
	const canBuyPack = computed<boolean>(() => !!state.value?.can_buy_pack);

	/**
	 * Состояние резерва всегда перечитывается: оно привязано к пользователю и
	 * меняется от покупки пакета до окончания подписки. Кэш здесь означал бы,
	 * что после смены аккаунта в той же вкладке новый пользователь увидит чужие
	 * цифры. Повторные одновременные вызовы схлопываются в один запрос.
	 */
	const load = (): Promise<void> => {
		// `GET /reserve` требует токен, а страница оплаты открыта и без входа:
		// без сессии не спрашиваем вовсе, иначе каждый аноним получает ошибку
		// в консоли. Состояние сбрасываем — в этой вкладке мог быть выход.
		if (!authApi.methods.getToken()) {
			state.value = null;
			return Promise.resolve();
		}

		if (statePending) return statePending;

		statePending = reserveApi.http.getState()
			.then((data) => {
				state.value = data;
			})
			.catch((error) => {
				// Резерв не выдан большинству пользователей и живёт за настройкой бота,
				// поэтому неответ — обычное дело: молча показываем кабинет без резерва.
				console.info('reserve state unavailable', error);
				state.value = null;
			})
			.finally(() => {
				statePending = null;
			});

		return statePending;
	};

	/** Каталог пакетов: цены не должны устаревать за сессию, поэтому тоже без кэша. */
	const loadPacks = (): Promise<void> => {
		if (packsPending) return packsPending;

		packsPending = reserveApi.http.getPacks()
			.then((data) => {
				packs.value = data ?? [];
			})
			.catch((error) => {
				console.info('reserve packs unavailable', error);
				packs.value = [];
			})
			.finally(() => {
				packsPending = null;
			});

		return packsPending;
	};

	// Что показывать: выданный резерв и сгоревший — у второго страница ведёт
	// продлевать подписку. Состояние `none` (резерва не было) не показываем.
	const isVisible = computed<boolean>(() => isIssued.value || isBurned.value);

	return {
		state,
		packs,
		remaining,
		isIssued,
		isVisible,
		isGrace,
		isBurned,
		isInactive,
		savedBytes,
		graceUntil,
		monthlyGb,
		isLimited,
		hasWhitelistData,
		isApproximate,
		grantedBytes,
		remainingBytes,
		remainingPercent,
		usedPercent,
		isExhausted,
		nextAccrualAt,
		nextEvent,
		canBuyPack,
		load,
		loadPacks
	};
});
